import React, { useState, useEffect } from 'react';
import { teamsAPI, matchesAPI } from '../../services/api';
import { FaTrophy, FaArrowUp, FaArrowDown, FaMinus, FaSync, FaChartLine } from 'react-icons/fa';

const StandingsSection = () => {
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState({});
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsRes, matchesRes] = await Promise.all([
        teamsAPI.getAll(),
        matchesAPI.getAll()
      ]);
      setTeams(teamsRes.data);
      setMatches(matchesRes.data);
      calculateStandings(teamsRes.data, matchesRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStandings = (teamsData, matchesData) => {
    const teamStats = {};
    
    // Initialiser les stats pour chaque équipe
    teamsData.forEach(team => {
      // Ignorer les équipes sans groupe assigné
      if (!team.group_number) {
        console.warn(`Équipe sans groupe: ${team.name} (ID: ${team.id})`);
        return;
      }
      
      teamStats[team.id] = {
        id: team.id,
        name: team.name,
        logo_url: team.logo_url,
        group: team.group_number,
        played: 0,
        won: 0,
        drawn: 0,
        lost: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
        points: 0,
        form: [] // 5 derniers matchs
      };
    });

    // Calculer les stats à partir des matchs terminés
    matchesData
      .filter(m => m.status === 'finished')
      .sort((a, b) => new Date(a.match_date) - new Date(b.match_date))
      .forEach(match => {
        const homeTeam = teamStats[match.home_team_id];
        const awayTeam = teamStats[match.away_team_id];

        if (homeTeam && awayTeam) {
          homeTeam.played++;
          awayTeam.played++;
          homeTeam.goalsFor += match.home_score;
          homeTeam.goalsAgainst += match.away_score;
          awayTeam.goalsFor += match.away_score;
          awayTeam.goalsAgainst += match.home_score;

          // Déterminer le résultat
          let homeResult, awayResult;
          if (match.home_score > match.away_score) {
            homeTeam.won++;
            homeTeam.points += 3;
            awayTeam.lost++;
            homeResult = 'W';
            awayResult = 'L';
          } else if (match.home_score < match.away_score) {
            awayTeam.won++;
            awayTeam.points += 3;
            homeTeam.lost++;
            homeResult = 'L';
            awayResult = 'W';
          } else {
            homeTeam.drawn++;
            awayTeam.drawn++;
            homeTeam.points += 1;
            awayTeam.points += 1;
            homeResult = 'D';
            awayResult = 'D';
          }

          // Ajouter à la forme (5 derniers matchs)
          homeTeam.form.push(homeResult);
          if (homeTeam.form.length > 5) homeTeam.form.shift();
          
          awayTeam.form.push(awayResult);
          if (awayTeam.form.length > 5) awayTeam.form.shift();

          homeTeam.goalDifference = homeTeam.goalsFor - homeTeam.goalsAgainst;
          awayTeam.goalDifference = awayTeam.goalsFor - awayTeam.goalsAgainst;
        }
      });

    // Organiser par groupe et trier
    const standingsByGroup = {};
    Object.values(teamStats).forEach(team => {
      const group = team.group;
      if (!standingsByGroup[group]) {
        standingsByGroup[group] = [];
      }
      standingsByGroup[group].push(team);
    });

    // Trier chaque groupe
    Object.keys(standingsByGroup).forEach(group => {
      standingsByGroup[group].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
        return b.goalsFor - a.goalsFor;
      });
    });

    setStandings(standingsByGroup);
  };

  const getFormIcon = (result) => {
    switch(result) {
      case 'W': return { icon: '●', color: '#28a745', label: 'Victoire' };
      case 'D': return { icon: '●', color: '#ffc107', label: 'Nul' };
      case 'L': return { icon: '●', color: '#dc3545', label: 'Défaite' };
      default: return { icon: '○', color: '#ccc', label: 'N/A' };
    }
  };

  const getPositionChange = (position, totalTeams) => {
    if (position === 1) return { icon: <FaArrowUp />, color: '#28a745', label: 'Leader' };
    if (position === 2) return { icon: <FaArrowUp />, color: '#17a2b8', label: 'Promotion' };
    if (position === totalTeams) return { icon: <FaArrowDown />, color: '#dc3545', label: 'Relégation' };
    return { icon: <FaMinus />, color: '#6c757d', label: 'Stable' };
  };

  const groups = Object.keys(standings).sort((a, b) => a - b);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <FaTrophy style={styles.titleIcon} />
            Classement & Points
          </h1>
          <p style={styles.subtitle}>
            Gestion automatique: Victoire = 3 pts | Nul = 1 pt | Défaite = 0 pt
          </p>
        </div>
        <button onClick={loadData} style={styles.refreshBtn} disabled={loading}>
          <FaSync style={{...styles.btnIcon, animation: loading ? 'spin 1s linear infinite' : 'none'}} />
          Actualiser
        </button>
      </div>

      {/* Statistiques Globales */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{...styles.statIcon, background: '#007bff'}}>
            <FaChartLine />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>
              {matches.filter(m => m.status === 'finished').length}
            </div>
            <div style={styles.statLabel}>Matchs Joués</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statIcon, background: '#28a745'}}>
            <FaTrophy />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>
              {Object.values(standings).flat().reduce((sum, t) => sum + t.won, 0)}
            </div>
            <div style={styles.statLabel}>Victoires Totales</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statIcon, background: '#ffc107'}}>
            <FaMinus />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>
              {Object.values(standings).flat().reduce((sum, t) => sum + t.drawn, 0)}
            </div>
            <div style={styles.statLabel}>Matchs Nuls</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{...styles.statIcon, background: '#dc3545'}}>
            <FaArrowDown />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>
              {Object.values(standings).flat().reduce((sum, t) => sum + t.goalsFor, 0)}
            </div>
            <div style={styles.statLabel}>Buts Marqués</div>
          </div>
        </div>
      </div>

      {/* Filtres par Groupe */}
      <div style={styles.filterSection}>
        <button
          onClick={() => setSelectedGroup('all')}
          style={{
            ...styles.filterBtn,
            ...(selectedGroup === 'all' ? styles.filterBtnActive : {})
          }}
        >
          Tous les Groupes
        </button>
        {groups.map(group => (
          <button
            key={group}
            onClick={() => setSelectedGroup(group)}
            style={{
              ...styles.filterBtn,
              ...(selectedGroup === group ? styles.filterBtnActive : {})
            }}
          >
            Groupe {group}
          </button>
        ))}
      </div>

      {/* Tableaux de Classement */}
      <div style={styles.standingsContainer}>
        {groups.length === 0 ? (
          <div style={styles.emptyState}>
            <h3>Aucun classement disponible</h3>
            <p>Les équipes doivent avoir un groupe assigné (group_number) pour apparaître dans le classement.</p>
            <p>Veuillez vérifier que toutes les équipes ont été correctement classées par groupe.</p>
          </div>
        ) : groups
          .filter(group => selectedGroup === 'all' || selectedGroup === group)
          .map(group => (
            <div key={group} style={styles.groupCard}>
              <div style={styles.groupHeader}>
                <h2 style={styles.groupTitle}>Groupe {group}</h2>
                <span style={styles.groupInfo}>
                  {standings[group].length} équipes • {standings[group].reduce((sum, t) => sum + t.played, 0)} matchs
                </span>
              </div>

              <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={{...styles.th, width: '50px'}}>Pos</th>
                      <th style={{...styles.th, width: '50px'}}></th>
                      <th style={{...styles.th, textAlign: 'left'}}>Équipe</th>
                      <th style={styles.th}>J</th>
                      <th style={styles.th}>V</th>
                      <th style={styles.th}>N</th>
                      <th style={styles.th}>D</th>
                      <th style={styles.th}>BP</th>
                      <th style={styles.th}>BC</th>
                      <th style={styles.th}>Diff</th>
                      <th style={{...styles.th, fontWeight: 'bold'}}>Pts</th>
                      <th style={styles.th}>Forme</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings[group].map((team, index) => {
                      const posChange = getPositionChange(index + 1, standings[group].length);
                      return (
                        <tr 
                          key={team.id}
                          style={{
                            ...styles.tableRow,
                            ...(index === 0 ? styles.firstPlace : {}),
                            ...(index === 1 ? styles.secondPlace : {}),
                            ...(index === standings[group].length - 1 ? styles.lastPlace : {})
                          }}
                        >
                          <td style={styles.td}>
                            <div style={styles.positionCell}>
                              <span style={styles.positionNumber}>{index + 1}</span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={{...styles.positionIndicator, color: posChange.color}}>
                              {posChange.icon}
                            </div>
                          </td>
                          <td style={{...styles.td, textAlign: 'left'}}>
                            <div style={styles.teamCell}>
                              {team.logo_url ? (
                                <img 
                                  src={team.logo_url}
                                  alt={team.name}
                                  style={styles.teamLogo}
                                  onError={(e) => {
                                    console.error(`Logo non trouvé: ${team.logo_url}`);
                                    e.target.style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div style={styles.noLogo}>
                                  {team.name.charAt(0)}
                                </div>
                              )}
                              <span style={styles.teamName}>{team.name}</span>
                            </div>
                          </td>
                          <td style={styles.td}>{team.played}</td>
                          <td style={{...styles.td, color: '#28a745', fontWeight: 'bold'}}>{team.won}</td>
                          <td style={{...styles.td, color: '#ffc107', fontWeight: 'bold'}}>{team.drawn}</td>
                          <td style={{...styles.td, color: '#dc3545', fontWeight: 'bold'}}>{team.lost}</td>
                          <td style={styles.td}>{team.goalsFor}</td>
                          <td style={styles.td}>{team.goalsAgainst}</td>
                          <td style={{
                            ...styles.td,
                            color: team.goalDifference > 0 ? '#28a745' : 
                                   team.goalDifference < 0 ? '#dc3545' : '#666',
                            fontWeight: 'bold'
                          }}>
                            {team.goalDifference > 0 ? '+' : ''}{team.goalDifference}
                          </td>
                          <td style={{...styles.td, fontWeight: 'bold', fontSize: '16px', color: '#c41e3a'}}>
                            {team.points}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.formCell}>
                              {team.form.map((result, i) => {
                                const formData = getFormIcon(result);
                                return (
                                  <span
                                    key={i}
                                    style={{...styles.formDot, color: formData.color}}
                                    title={formData.label}
                                  >
                                    {formData.icon}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Légende */}
              <div style={styles.legend}>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendBox, background: '#d4edda'}}></div>
                  <span>Promotion</span>
                </div>
                <div style={styles.legendItem}>
                  <div style={{...styles.legendBox, background: '#f8d7da'}}></div>
                  <span>Relégation</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#28a745', fontSize: '18px'}}>●</span>
                  <span>Victoire</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#ffc107', fontSize: '18px'}}>●</span>
                  <span>Nul</span>
                </div>
                <div style={styles.legendItem}>
                  <span style={{color: '#dc3545', fontSize: '18px'}}>●</span>
                  <span>Défaite</span>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* Info Box */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>ℹ️ Calcul Automatique des Points</h3>
        <div style={styles.infoContent}>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Victoire:</span>
            <span style={styles.infoValue}>+3 points</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Match Nul:</span>
            <span style={styles.infoValue}>+1 point</span>
          </div>
          <div style={styles.infoItem}>
            <span style={styles.infoLabel}>Défaite:</span>
            <span style={styles.infoValue}>0 point</span>
          </div>
        </div>
        <p style={styles.infoNote}>
          💡 Le classement se met à jour automatiquement dès qu'un match est marqué comme "terminé" avec un score.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '20px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  titleIcon: {
    color: '#c41e3a',
    fontSize: '32px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginTop: '5px'
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  btnIcon: {
    fontSize: '14px'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  statIcon: {
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: 'white'
  },
  statContent: {
    flex: 1
  },
  statNumber: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333'
  },
  statLabel: {
    fontSize: '13px',
    color: '#666',
    marginTop: '2px'
  },
  filterSection: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    flexWrap: 'wrap'
  },
  filterBtn: {
    padding: '10px 20px',
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  filterBtnActive: {
    background: '#c41e3a',
    color: 'white',
    borderColor: '#c41e3a'
  },
  standingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  groupCard: {
    background: 'white',
    borderRadius: '15px',
    padding: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  groupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid #c41e3a'
  },
  groupTitle: {
    fontSize: '22px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  groupInfo: {
    fontSize: '14px',
    color: '#666'
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  tableHeader: {
    background: '#f8f9fa',
    borderBottom: '2px solid #dee2e6'
  },
  th: {
    padding: '12px 8px',
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase'
  },
  tableRow: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s'
  },
  firstPlace: {
    background: 'rgba(212, 237, 218, 0.3)'
  },
  secondPlace: {
    background: 'rgba(212, 237, 218, 0.15)'
  },
  lastPlace: {
    background: 'rgba(248, 215, 218, 0.3)'
  },
  td: {
    padding: '12px 8px',
    textAlign: 'center',
    fontSize: '14px',
    color: '#333'
  },
  positionCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  positionNumber: {
    fontWeight: 'bold',
    fontSize: '15px'
  },
  positionIndicator: {
    fontSize: '16px'
  },
  teamCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  teamLogo: {
    width: '28px',
    height: '28px',
    objectFit: 'contain',
    flexShrink: 0
  },
  noLogo: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#c41e3a',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '14px',
    flexShrink: 0
  },
  teamName: {
    fontWeight: '600',
    fontSize: '14px',
    flex: 1
  },
  formCell: {
    display: 'flex',
    gap: '4px',
    justifyContent: 'center'
  },
  formDot: {
    fontSize: '16px',
    cursor: 'help'
  },
  emptyState: {
    background: 'white',
    padding: '60px 20px',
    borderRadius: '15px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  legend: {
    display: 'flex',
    gap: '20px',
    marginTop: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center'
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px'
  },
  legendBox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px'
  },
  infoBox: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '25px',
    borderRadius: '15px',
    marginTop: '30px'
  },
  infoTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '15px'
  },
  infoContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    marginBottom: '15px'
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '8px'
  },
  infoLabel: {
    fontWeight: '600'
  },
  infoValue: {
    fontWeight: 'bold',
    fontSize: '16px'
  },
  infoNote: {
    fontSize: '14px',
    opacity: 0.9,
    margin: 0
  }
};

// Animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .tableRow:hover {
    background: rgba(196, 30, 58, 0.05) !important;
  }

  .refreshBtn:hover {
    background: #0056b3;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  }

  .filterBtn:hover {
    border-color: #c41e3a;
    color: #c41e3a;
  }
`;
document.head.appendChild(styleSheet);

export default StandingsSection;
